import { ReactElement } from "react";
import { CloseIcon } from "src/foundations/icons";
import {
    BottomComponentBox,
    HeaderBox,
    HeaderTitle,
    MainComponentBox,
    Puller,
    ScrollableBox,
    CompoundHeaderBox,
    TitleBox,
    TopComponentBox,
    HeaderDivider,
} from "src/components/modal/senec-drawer/SenecDrawerContent.styled";
import { Box, Divider, Stack } from "@mui/material";
import { SenecTooltip, TooltipDetails } from "src/components/utils/senec-tooltip/SenecTooltip";
import { useScreenSize } from "src/hooks/useScreenSize";
import { SenecFunctionalIconButton } from "src/components/button/senec-functional-button/SenecFunctionalIconButton";

export interface SenecDrawerContentProps {
    title?: string;
    tooltip?: TooltipDetails;
    TopComponent?: ReactElement;
    MainComponent?: ReactElement;
    BottomComponent?: ReactElement;
    HeaderActionsComponent?: ReactElement;
    closeDrawerOnClick: () => void;
    showDivider: boolean;
    "data-testid": string;
}

export const SenecDrawerContent = ({
    title,
    tooltip,
    TopComponent,
    MainComponent,
    BottomComponent,
    HeaderActionsComponent,
    closeDrawerOnClick,
    showDivider,
    "data-testid": dataTestId,
}: SenecDrawerContentProps) => {
    return (
        <Box data-testid={dataTestId} display={"flex"} flex={1} flexDirection={"column"}>
            <Puller />
            <DrawerHeader
                data-testid={`${dataTestId}-header`}
                title={title}
                TopComponent={TopComponent}
                tooltip={tooltip}
                closeDrawerOnClick={closeDrawerOnClick}
                HeaderActionsComponent={HeaderActionsComponent}
            />
            {showDivider && <HeaderDivider data-testid={"main-divider"} />}
            <ScrollableBox>
                <MainComponentBox data-testid={`${dataTestId}-main-content`}>{MainComponent}</MainComponentBox>
            </ScrollableBox>
            {BottomComponent && (
                <>
                    <Divider />
                    <BottomComponentBox data-testid={`${dataTestId}-bottom-content`}>{BottomComponent}</BottomComponentBox>
                </>
            )}
        </Box>
    );
};

const DrawerHeader = ({
    "data-testid": dataTestId,
    title,
    TopComponent,
    tooltip,
    closeDrawerOnClick,
    HeaderActionsComponent,
}: {
    "data-testid": string;
    title?: string;
    TopComponent?: ReactElement;
    tooltip?: TooltipDetails;
    closeDrawerOnClick: () => void;
    HeaderActionsComponent?: ReactElement;
}) => {
    return (
        <CompoundHeaderBox flexDirection="column">
            <HeaderBox data-testid={`${dataTestId}-header`}>
                <TitleBox>
                    <HeaderTitle variant={"h4"}>{title}</HeaderTitle>
                    <DrawerTooltip tooltip={tooltip} />
                </TitleBox>
                <Stack gap={1.5} direction={"row"}>
                    {HeaderActionsComponent && <>{HeaderActionsComponent}</>}
                    <SenecFunctionalIconButton
                        level="secondary"
                        data-testid={"close-btn-id"}
                        size="medium"
                        aria-label="back"
                        onClick={closeDrawerOnClick}
                    >
                        <CloseIcon />
                    </SenecFunctionalIconButton>
                </Stack>
            </HeaderBox>
            {TopComponent && <TopComponentBox data-testid={`${dataTestId}-top-content`}>{TopComponent}</TopComponentBox>}
        </CompoundHeaderBox>
    );
};

const DrawerTooltip = ({ tooltip }: { tooltip?: TooltipDetails }) => {
    const { isMobile } = useScreenSize();

    return (
        <>
            {tooltip && (
                <Box sx={{ paddingTop: isMobile ? 0.5 : 1, paddingLeft: 1 }}>
                    <SenecTooltip tooltip={{ tooltipId: tooltip.tooltipId, text: tooltip.text }} />
                </Box>
            )}
        </>
    );
};
